// src/pages/QRDesignPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { CardService } from '../services/cardService';
import { QRService, QRDesignData } from '../services/qrService';
import QRCode from '../components/QRCode';
import ColorPicker from '../components/ColorPicker';

// 모바일 앱과 동일한 색상 옵션
const PREDEFINED_COLORS = [
  { name: 'pureBlack', value: '#000000' },
  { name: 'coolMint', value: '#00C2A8' },
  { name: 'deepBlue', value: '#1E3A8A' },
  { name: 'sunsetOrange', value: '#FF6B57' },
  { name: 'mutedViolet', value: '#7D6AFF' },
  { name: 'warmSand', value: '#F4D58D' },
  { name: 'modernBlue', value: '#3B82F6' },
  { name: 'vibrantGreen', value: '#10B981' },
  { name: 'coralRed', value: '#EF4444' },
  { name: 'royalPurple', value: '#8B5CF6' },
];

const BACKGROUND_COLORS = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F2F2F7' },
  { name: 'Light Blue', value: '#E6F3FF' },
  { name: 'Light Green', value: '#E6FFED' },
  { name: 'Light Pink', value: '#FFE6F0' },
  { name: 'Light Yellow', value: '#FFFACD' },
];

const QRDesignPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cardId } = useParams<{ cardId: string }>();

  const [qrColor, setQrColor] = useState('#000000');
  const [qrBackgroundColor, setQrBackgroundColor] = useState('#FFFFFF');
  const [useProfileImageAsLogo, setUseProfileImageAsLogo] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [cardData, setCardData] = useState<any>(null);
  const [qrValue, setQrValue] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 안전한 번역 함수
  const safeT = (key: string, fallback: string) => {
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    const loadCardData = async () => {
      if (!user?.uid || !cardId) return;

      try {
        const cards = await CardService.getUserCards(user.uid);
        const card = cards.find(c => c.id === cardId);
        
        if (!card) {
          console.error('Card not found:', cardId);
          return;
        }
        
        setCardData(card);
        
        const url = card.customUrl
          ? `https://scanvcf.com/${card.customUrl}`
          : `https://scanvcf.com/qr/${card.id}`;
        setQrValue(url);

        // 기존 QR 디자인 데이터 마이그레이션
        await QRService.migrateQRDesignData(user.uid, cardId);
        
        const qrDesign = await QRService.getQRDesign(user.uid, cardId);
        if (qrDesign) {
          setQrColor(qrDesign.qrColor);
          setQrBackgroundColor(qrDesign.qrBackgroundColor);
          setCustomLogoUrl(qrDesign.qrLogoUrl || null);
        }
      } catch (error) {
        console.error('Failed to load card data:', error);
      }
    };

    loadCardData();
  }, [user?.uid, cardId]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomLogoUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleReset = () => {
    setQrColor('#000000');
    setQrBackgroundColor('#FFFFFF');
    setCustomLogoUrl(null);
    setUseProfileImageAsLogo(false);
  };

  const handleSave = async () => {
    if (!user?.uid || !cardId) return;

    setIsSaving(true);
    try {
      const logoUrl = customLogoUrl || (useProfileImageAsLogo && cardData?.photoUrl ? cardData.photoUrl : null);

      const qrDesignData: QRDesignData = {
        qrColor,
        qrBackgroundColor,
        qrLogoUrl: logoUrl || undefined,
        qrValue,
        createdAt: new Date()
      };

      await QRService.updateQRDesign(user.uid, cardId, qrDesignData);

      alert(safeT('qrDesign.saveComplete', 'QR design has been saved.'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save QR design:', error);
      alert(safeT('qrDesign.saveError', 'An error occurred while saving.'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!cardData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{safeT('common.loading', 'Loading card information...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {safeT('qrDesign.title', 'QR Code Design')}
          </h1>
          <p className="text-gray-600">
            {safeT('qrDesign.subtitle', 'Customize the color and logo of your QR code')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* QR 코드 미리보기 */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('profilePreview.title', 'Preview')}
            </h2>
            <div 
              className="flex justify-center p-6 rounded-lg"
              style={{ backgroundColor: qrBackgroundColor }}
            >
              <QRCode
                value={qrValue}
                size={256}
                color={qrColor}
                backgroundColor={qrBackgroundColor}
                logoUrl={customLogoUrl || (useProfileImageAsLogo && cardData?.photoUrl ? cardData.photoUrl : undefined)}
                logoSize={80}
                logoBackgroundColor="white"
                logoMargin={8}
                logoBorderRadius={8}
                ecl="H"
              />
            </div>
          </div>

          {/* 디자인 옵션 */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('qrDesign.colorSelectionTitle', 'Color Selection')}
            </h2>

            {/* QR 코드 색상 */}
            <div className="mb-6">
              <ColorPicker
                color={qrColor}
                onColorChange={setQrColor}
                showSwatches={true}
                swatches={PREDEFINED_COLORS}
                label={safeT('qrDesign.qrCodeColor', 'QR Code Color')}
              />
            </div>

            {/* 배경 색상 */}
            <div className="mb-6">
              <ColorPicker
                color={qrBackgroundColor}
                onColorChange={setQrBackgroundColor}
                showSwatches={true}
                swatches={BACKGROUND_COLORS}
                label={safeT('qrDesign.backgroundColor', 'Background Color')}
              />
            </div>

            {/* 로고 옵션 */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-700 mb-3">
                {safeT('qrDesign.logoSectionTitle', 'Logo Settings')}
              </h3>
              
              {/* 프로필 이미지 사용 */}
              {cardData?.photoUrl && (
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={useProfileImageAsLogo}
                      onChange={(e) => setUseProfileImageAsLogo(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      {safeT('qrDesign.useProfileAsLogo', 'Use profile image as logo')}
                    </span>
                  </label>
                </div>
              )}

              {/* 커스텀 로고 업로드 */}
              <div className="mb-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  {safeT('qrDesign.customLogoButton', 'Upload Custom Logo')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>

              {/* 선택된 로고 미리보기 */}
              {customLogoUrl && (
                <div className="flex items-center p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-600 mr-3">
                    {safeT('qrDesign.selectedLogoLabel', 'Selected Logo:')}
                  </span>
                  <img
                    src={customLogoUrl}
                    alt="Selected logo"
                    className="w-10 h-10 rounded object-cover"
                  />
                </div>
              )}
            </div>

            {/* 액션 버튼들 */}
            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              >
                {safeT('qrDesign.resetAllButton', 'Reset')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? safeT('qrDesign.saving', 'Saving...') : safeT('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRDesignPage;